import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConsecutiveIllust from '@/components/atoms/ConsecutiveIllust';
import VolumeChecker from '@/components/atoms/VolumeChecker';
import useAudioInputTester from '@/components/organisms/audioTest/useAudioInputTester';
import CountdownStopTimer from '@/components/organisms/chat/CountdownStopTimer';

export type ChatConsecutiveVoiceDialogProps = {
  open: boolean;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  page: 'recording' | 'recognizing' | 'translating';
};

const ChatConsecutiveVoiceDialog = (props: ChatConsecutiveVoiceDialogProps) => {
  const { t } = useTranslation('chat');
  const audioTester = useAudioInputTester();
  const audioStart = audioTester.start;
  const audioStop = audioTester.stop;
  const open = props.open;

  useEffect(() => {
    if (open) {
      audioStart();
    } else {
      audioStop();
    }
  }, [audioStart, audioStop, open]);

  // ダイアログが閉じるときに前のページが表示されないよう、ページ順の制御
  const [page, setPage] = useState(props.page);
  useEffect(() => {
    if (open) {
      setPage('recording');
    }
  }, [open]);
  useEffect(() => {
    if (props.page === 'recognizing' && page === 'recording') {
      setPage(props.page);
    } else if (props.page === 'translating' && page === 'recognizing') {
      setPage(props.page);
    }
  }, [props.page, page]);

  return (
    <Dialog
      open={props.open}
      PaperProps={{
        sx: {
          maxWidth: 300,
          position: 'fixed',
          height: 500,
          bottom: 0,
        },
      }}
    >
      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        <IconButton
          aria-label="close"
          onClick={props.onCancelRecording}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack
          alignItems={'center'}
          sx={{
            height: '100%',
          }}
        >
          <Stack
            bgcolor={'#e9f4fbff'}
            width={'100%'}
            maxHeight={'50%'}
            alignItems={'center'}
            pt={2}
          >
            {page === 'recording' && (
              <>
                <Typography variant="h4">{t('recording')}</Typography>
                <Typography variant="body1" py={2}>
                  {t('speakCloser')}
                </Typography>
                <ConsecutiveIllust />
              </>
            )}
            {page === 'recognizing' && (
              <Typography variant="h4" py={10}>
                {t('recognizing')}
              </Typography>
            )}
            {page === 'translating' && (
              <Typography variant="h4" py={10}>
                {t('translating')}
              </Typography>
            )}
          </Stack>
          <Box
            sx={{ height: '50%', position: 'relative', display: 'contents' }}
            p={1}
          >
            {page === 'recording' && (
              <>
                <CountdownStopTimer
                  stop={() => {
                    // 停止処理
                    props.onStopRecording();
                  }}
                  ms={20000}
                />
                <VolumeChecker valueRef={audioTester.volume} />
              </>
            )}
            {page === 'recognizing' && (
              <Box pt={'120px'}>
                <CircularProgress />
              </Box>
            )}
            {page === 'translating' && (
              <Box pt={'120px'}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
export default ChatConsecutiveVoiceDialog;
